import * as React from 'react';
import './ProfessionalForm'
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { InputGroup, InputGroupAddon } from 'reactstrap';
import { ProfessionalPayment, ProfessionalPaymentType } from '../../../models';
import { hide } from '../../../shared/style';
import { getPaymentsSettings } from '../../../store/settings/selectors';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AutocompleteTextField, NumberInputField } from '../fields';

export interface ProfessionalPaymentSingleProps {
    id: string,
    data: ProfessionalPayment,
    types?: ProfessionalPaymentType[],
    onChange: (data: ProfessionalPayment) => void,
}

const getInputAddon = (type: ProfessionalPaymentType): string|undefined => {
    switch(type) {
        case ProfessionalPaymentType.Fixed: return '€'
        case ProfessionalPaymentType.PerHour: return '€/h'
        case ProfessionalPaymentType.Percentage: return '%'
        case ProfessionalPaymentType.PercentageEffective: return '%'
        default: return undefined
    }
}

export const ProfessionalPaymentSingleInput: React.FC<ProfessionalPaymentSingleProps> = ({
    id,
    data,
    types = [
        ProfessionalPaymentType.Fixed,
        ProfessionalPaymentType.PerHour,
        ProfessionalPaymentType.Percentage,
        ProfessionalPaymentType.PercentageEffective,
        ProfessionalPaymentType.Profile,
    ],
    onChange,
}) => {
    const t = useTranslations()

    const _profiles = useSelector(getPaymentsSettings)

    const profiles = useMemo(() => _profiles.filter(p => !_profiles.some(pp => pp.id === p.id && pp.version > p.version)), [_profiles])

    const inputAddon = useMemo(() => getInputAddon(data.type), [data.type])

    const typeMap = useMemo<Record<string, ProfessionalPaymentType>>(() => ({
        [t(TK.percentageAppointmentPrice)]: ProfessionalPaymentType.Percentage,
        [t(TK.percentageAmountPaid)]: ProfessionalPaymentType.PercentageEffective,
        [t(TK.perHour)]: ProfessionalPaymentType.PerHour,
        [t(TK.fixedValue)]: ProfessionalPaymentType.Fixed,
        [t(TK.paymentProfile)]: ProfessionalPaymentType.Profile,
    }), [t])
    const typeValue = useMemo(() => Object.entries(typeMap).find(([_, val]) => val === data.type)?.[0], [data.type])
    const typeOptions = useMemo(() => Object.entries(typeMap).filter(([_, val]) => types.includes(val)).map(([key]) => key), [typeMap])

    const showValue = useMemo(() => data.type && data.type !== ProfessionalPaymentType.Profile, [data.type])
    const showProfile = useMemo(() => data.type === ProfessionalPaymentType.Profile, [data.type])

    return (
        <div className='paymentField'>
            <AutocompleteTextField 
                className='paymentFieldType'
                id={`${id}-type-input`}
                value={typeValue} 
                options={typeOptions} 
                onValueChanged={type => onChange({...data, type: typeMap[type]})} />
            <NumberInputField 
                id={`${id}-value`}
                style={hide(!showValue)}
                append={inputAddon}
                max={data.type === ProfessionalPaymentType.Percentage ? 100 : undefined}
                min={0}
                value={data.value}
                onChange={value => onChange({...data, profileId: undefined, value})} />
            <AutocompleteTextField
                className='paymentFieldProfile'
                style={hide(!showProfile)} 
                id={`${id}-type-profile`}
                value={profiles.find(p => p.id === data.profileId)?.name ?? data.profileId}
                onValueChanged={name => onChange({...data, value: undefined, profileId: profiles.find(p => p.name === name)?.id})} 
                options={profiles.map(p => p.name)} /> 
        </div>
    )
}